import React, { FC, useMemo, useState } from 'react'

import classNames from 'classnames'
import { differenceInCalendarDays } from 'date-fns'
import { orderBy as _orderBy } from 'lodash'

import Modal from '../Modals/Modal'
import { Breadcrumbs } from './Breadcrumbs'
import DocusignDrawer from './DocusignDrawer'
import { FilesViewerList } from './FilesViewerList'
import { FolderMenu } from './FolderMenu'
import { SearchFilter } from './SearchFilter'
import { TreeViewer } from './TreeViewer'
import {
  FileManagerFile,
  MODALS,
  ROOT_FOLDER,
  SERVICING_FOLDER,
  clearFiles,
  setActiveModal,
} from './fileManagerSlice'
import css from './styles.module.css'
import useDocuments from './useDocuments'
import { useAppDispatch, useAppSelector } from '@/client/hooks'
import { FileDeleteModal } from '@/features/FileManager/FileDeleteModal'
import { FileEditModal } from '@/features/FileManager/FileEditModal'
import { FileRenameModal } from '@/features/FileManager/FileRenameV2'
import { FileUploadModal } from '@/features/FileManager/FileUploadModalV2'
import { downloadManyFiles, handleOpenFile, openMaxViewer } from '@/features/FileManager/utilities'
import { useCurrentApplication } from '@/graphql/services/application'
import { useGetLoggedUserQuery } from '@/services/user'
import { useListApplicationProfilesQuery } from '@/services/v2-application/applicationProfile'
import { useDropFile } from '@/utils/useDropFile'
import {
  isSalesUserAndAppNotInPendingICSDelivery,
  isUserAllowedToSendToDocuSign,
} from '@/utils/user'

export const CUSTOMER_FOLDER = 'CX Docs'
export const STATEMENTS_FOLDER = 'Statements'
export const DELETED_FOLDER = 'Deleted'

export const ROOT_FOLDERS_CHILDREN = {
  [ROOT_FOLDER]: {
    sortAlphabetically: true,
    children: [
      CUSTOMER_FOLDER,
      'Co-Owner Docs',
      'Client Signed Docs',
      'Contract Docs',
      'Expired / Not Needed',
      'HEO Docs',
      'Inspection Docs',
      'AVM Docs',
      'Appraisal Docs',
      'Title Docs',
      'Payoff Docs',
      'Post-Closing Docs',
      'z-Raw Data',
      // Dynamic folders, might get hidden on certain conditions by calculateDynamicFolders
      'DTI Docs',
      'Digital Certificates',
      'HOA Docs',
      'Rental Docs',
      'Solar Docs',
      'POA Docs',
      'Deferment-Forbearance Docs',
      'NOO Docs',
      'Trust Docs',
    ],
  },
  [SERVICING_FOLDER]: {
    sortAlphabetically: false,
    children: [
      STATEMENTS_FOLDER,
      'Buyout Docs',
      'Home Sale Docs',
      'Subordination Docs',
      'Unlock Refi Docs',
      'Home Improvement Docs',
      'Generated Docs',
      'Other Docs',
    ],
  },
}

export const FileManager: FC = () => {
  const { data: application } = useCurrentApplication()

  const { data: applicationProfiles } = useListApplicationProfilesQuery(application?.uuid, {
    skip: !application?.uuid,
  })

  const applicantIds = useMemo(() => {
    return applicationProfiles?.results?.map((ap) => ap.id)
  }, [applicationProfiles])
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const dispatch = useAppDispatch()

  const { data: documents } = useDocuments()

  const {
    orderBy,
    search,
    searchAllFolders,
    selectedFolder,
    selectedParentFolder,
    activeModal,
    selectedFiles,
    isICSDocGenerated,
  } = useAppSelector((state) => state.fileManager)

  const [hoveringOpenFilesButton, setHoveringOpenFilesButton] = useState(false)

  // Used to preselect files when uploading via Drag and Drop
  const [preSelectedFiles, setPreSelectedFiles] = useState<File[]>([])

  const files = useMemo(() => {
    let output = documents

    // Filter by folder
    // When search term exists and searchAllFolders is on, don't filter by folder
    if (!(search && searchAllFolders)) {
      output = output.filter((file) => {
        const completeFolderPath = !selectedParentFolder
          ? selectedFolder
          : `${selectedParentFolder}/${selectedFolder}`

        return (selectedFolder === ROOT_FOLDER && !file.tag) || file.tag === completeFolderPath
      })
    }

    // Filter by search filter
    if (search) {
      const searchInName = (file): boolean => {
        return file.fileName?.toLowerCase().replaceAll(/_-/g, '').includes(search.toLowerCase())
      }

      output = output.filter((file) => searchInName(file))
    }

    // Don't show deleted files that haven't been touch in 30 days
    const today = new Date()
    output = output.filter((file) => {
      if (file.tag === DELETED_FOLDER) {
        const diff = differenceInCalendarDays(today, new Date(file.modifiedAt))
        return diff < 30
      } else {
        return true
      }
    })

    // Order by
    if (orderBy.label) {
      output = _orderBy(output, orderBy.label, orderBy.order) as FileManagerFile[]
    }

    return output
  }, [documents, search, orderBy, selectedFolder, searchAllFolders])

  const toggle = () => dispatch(setActiveModal(null))

  const ModalComponent = () => {
    return activeModal.type === 'file-upload' ? (
      <FileUploadModal
        applicantIds={applicantIds}
        selectedFolder={selectedFolder}
        selectedParentFolder={selectedParentFolder}
        preSelectedFiles={preSelectedFiles}
        setPreSelectedFiles={setPreSelectedFiles}
      />
    ) : activeModal.type === 'file-edit' ? (
      <FileEditModal
        documents={documents}
        mode={activeModal.mode}
      />
    ) : activeModal.type === 'file-delete' ? (
      <FileDeleteModal />
    ) : activeModal.type === 'file-rename' ? (
      <FileRenameModal />
    ) : null
  }

  const handleFilesDrop = async (files: File[]) => {
    setPreSelectedFiles(files)
    dispatch(setActiveModal(MODALS.Upload))
  }

  const { dropRef, isOver, validFiles } = useDropFile(handleFilesDrop)

  const dropZoneClasses = classNames({
    'border-2 border-sky-400 border-dashed': isOver,
    'bg-sky-300': isOver && validFiles,
    'bg-gray-400': isOver && !validFiles,
  })

  const { data: user } = useGetLoggedUserQuery()

  const _isSalesUserAndAppNotInPendingICSDelivery = isSalesUserAndAppNotInPendingICSDelivery({
    userGroups: user?.groups,
    applicationStateIndex: application?.applicationStateIndex,
  })

  const isSendToDocuSignEnabled = isUserAllowedToSendToDocuSign(user)

  return (
    <div
      className={`m-3 card border shadow bg-base-100 ${css.fileManager} grid grid-cols-[280px_1.1fr_1fr] overflow-auto bg-white shadow text-[13px] min-w-[650px] relative`}
    >
      <div
        className={`${css.contextMenu} flex justify-between items-center border-b p-3 sticky top-0 z-10 bg-white`}
      >
        <FolderMenu />
        <div className='flex gap-4 items-center'>
          <p className='font-bold'>{selectedFiles.length} file(s) selected</p>
          <button
            onClick={() => dispatch(clearFiles())}
            disabled={!selectedFiles.length}
            className='btn btn-primary btn-sm'
          >
            Clear All
          </button>
          <button
            onClick={() => downloadManyFiles(selectedFiles)}
            disabled={
              !selectedFiles.length ||
              (_isSalesUserAndAppNotInPendingICSDelivery && isICSDocGenerated)
            }
            className='btn btn-primary btn-sm'
          >
            Download All
          </button>
          <button
            className={`relative btn btn-primary btn-sm`}
            onMouseEnter={() => setHoveringOpenFilesButton(true)}
            onMouseLeave={() => setHoveringOpenFilesButton(false)}
            disabled={!selectedFiles.length}
          >
            Open All
            {!!selectedFiles.length && hoveringOpenFilesButton && (
              <div className='absolute top-0 menu bg-base-100 rounded-box w-max border right-0'>
                <p
                  className='p-1 text-right text-sky-400 text-sm hover:bg-sky-300 hover:rounded-md cursor-pointer'
                  onClick={() => {
                    selectedFiles.forEach((file) => handleOpenFile(file, setIsLoading))
                  }}
                >
                  {isLoading ? 'Loading files...' : 'Open in Browser'}
                </p>
                <p
                  className='p-1 text-right text-sky-400 text-sm hover:bg-sky-300 hover:rounded-md cursor-pointer'
                  onClick={() => openMaxViewer(application.externalId, selectedFiles)}
                >
                  Open in Max Viewer
                </p>
              </div>
            )}
          </button>
          <button
            onClick={() => dispatch(setActiveModal(MODALS.Delete))}
            disabled={!selectedFiles.length}
            className='btn btn-error btn-sm'
          >
            Delete All
          </button>

          <button
            onClick={() => dispatch(setActiveModal(MODALS.DocusignV2))}
            disabled={
              !selectedFiles.length ||
              !isSendToDocuSignEnabled ||
              (_isSalesUserAndAppNotInPendingICSDelivery && isICSDocGenerated)
            }
            className='btn btn-primary btn-sm'
          >
            Send to Docusign
          </button>
        </div>
      </div>
      <TreeViewer
        files={documents}
        application={application}
      />
      <div className={`${css.filesViewer}`}>
        <div className={`flex justify-between border-b px-3 py-2 h-20`}>
          <Breadcrumbs /> <SearchFilter />
        </div>
        <div
          id='drop-area'
          ref={dropRef}
          className={`relative h-80`}
        >
          {!files?.length ? (
            <p className='p-5 text-center w-full'>No documents to show.</p>
          ) : (
            <FilesViewerList files={files} />
          )}

          {isOver && (
            <div
              className={`${dropZoneClasses} top-0 left-0 absolute w-full h-full flex items-center justify-center z-50`}
            >
              {validFiles ? (
                <h4>Drop files to upload</h4>
              ) : (
                <div className='flex flex-col items-center'>
                  <h4>Warning: Some of your files are not supported</h4>
                  <br />
                  <h4>Please, only drag valid files</h4>
                  <br />
                  <p>Supported formats: jpg, gif, png, pdf, docx, xml</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      <div id='file-manager-modal' />

      <Modal
        visible={!!activeModal && !!activeModal.type && activeModal.type !== 'docusign-v2'}
        toggle={toggle}
        containerSelector='#file-manager-modal'
        modalCSS={activeModal?.css}
        title={activeModal?.title}
      >
        <ModalComponent />
      </Modal>
      <DocusignDrawer
        isOpen={activeModal?.type === 'docusign-v2'}
        setIsOpen={setActiveModal}
        application={application}
        useDispatch
      />
    </div>
  )
}
